import React, { useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { Field } from 'react-final-form';
import { get } from 'lodash';
import moment from 'moment';
import { AddOnBlock, Heading, Box, FeatureListItem, Button } from '@landtrustinc/design-system';
import {
  Form,
  FieldPartySizeSelect,
  FlexiblePartySizeSelect,
  FieldDates,
  PrimaryButton,
  FormValidationTooltip,
  CredovaPlugin,
  PackageItinerary,
} from '../..';
import { composeValidators, required } from '../../../util/validators';
import { isFlexiblePricingEnabled, handleLodgingAddOnChange } from '../../../util/package';
import { generateTransaction } from '../../../util/transaction';
import { convertMoneyToNumber } from '../../../util/currency';
import { getLodgingIcon } from '../../../util/lodging';
import EstimatedBreakdownMaybe from '../../../forms/BookingDatesForm/EstimatedBreakdownMaybe';
import PackageBadgesAvailability from './PackageBadgesAvailability';
import css from '../PackagePricingPanel.module.css';

export const getPackageScarcityMessage = packageInfo => {
  const { hasQuantityRestricted, quantity, activity } = packageInfo || {};
  const quantityBooked = quantity?.booked || 0;
  const quantityTotal = quantity?.total || 0;
  const quantityRemaining = quantityTotal - quantityBooked;

  if (!hasQuantityRestricted || quantityTotal <= 0 || quantityRemaining <= 0) {
    return null;
  }

  if (quantityBooked === 0) {
    return {
      id: 'PackagePricingPanel.scarcityOffered',
      isUrgent: false,
      values: {
        count: quantityTotal,
        activity: activity === 'hunt' ? 'hunt' : 'trip',
      },
    };
  }

  if (quantityRemaining > 0 && quantityBooked > 0) {
    return {
      id: 'PackagePricingPanel.scarcityRemaining',
      isUrgent: true,
      values: {
        remaining: quantityRemaining,
        booked: quantityBooked,
        total: quantityTotal,
        activity: activity === 'hunt' ? 'hunt' : 'trip',
      },
    };
  }

  return null;
};

const PackagePricingForm = ({
  handleSubmit,
  calendarInfoDays,
  bookingDateText,
  form,
  startDate,
  endDate,
  formattedStartDate,
  formattedEndDate,
  showLodgingAddOn,
  lodgingAddons,
  inProgress,
  submitDisabled,
  submitError,
  bookingData,
  onTransactionPriceChange,
  showCredova,
  setIsCredovaModalOpen,
  hasLodging,
  dynamicHasLodging,
  dateValues,
  availableDays,
  packageInfo,
  packageMinParty,
  packageMaxParty,
  timeSlots,
  listing,
  optionalLodging,
  intl,
  isAvailable,
  outOfBookingRange,
  formattedPackageAvailableDate,
  availabilityPackageBadges,
  setIsPackageAvailabilityModalOpen,
  isOnWaitingList,
  onMessage,
  onBookingSelectionChange,
  partySizeValue,
  landownerFirstName,
  isOwnListing,
}) => {
  const formFieldWrapperRef = useRef(null);
  const scarcityMessage = getPackageScarcityMessage(packageInfo);
  const isoOrEmpty = value => {
    if (!value) {
      return '';
    }
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? '' : date.toISOString();
  };
  const selectionKey = [
    isoOrEmpty(dateValues?.startDate),
    isoOrEmpty(dateValues?.endDate),
    isoOrEmpty(dateValues?.startLodgingDate),
    typeof partySizeValue === 'object' ? JSON.stringify(partySizeValue) : partySizeValue ?? '',
    Array.isArray(lodgingAddons) ? lodgingAddons.join(',') : '',
  ].join('|');

  useEffect(() => {
    if (typeof onBookingSelectionChange !== 'function') {
      return;
    }

    if (!dateValues?.startDate || !dateValues?.endDate) {
      onBookingSelectionChange(null);
      return;
    }

    onBookingSelectionChange({
      bookingDates: {
        startDate: dateValues.startDate,
        endDate: dateValues.endDate,
        startLodgingDate: dateValues.startLodgingDate || null,
      },
      partySize: partySizeValue,
      lodgingAddons: lodgingAddons || [],
    });
    // selectionKey is the stable snapshot of the fields above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionKey, onBookingSelectionChange]);

  const messageWithSelection = () => {
    if (typeof onMessage !== 'function') {
      return;
    }

    onMessage({
      bookingDates:
        dateValues?.startDate && dateValues?.endDate
          ? {
              startDate: dateValues.startDate,
              endDate: dateValues.endDate,
              startLodgingDate: dateValues.startLodgingDate || null,
            }
          : null,
      partySize: partySizeValue,
      lodgingAddons: lodgingAddons || [],
    });
  };

  if (!isAvailable || outOfBookingRange) {
    return (
      <PackageBadgesAvailability
        isAvailable={isAvailable}
        outOfBookingRange={outOfBookingRange}
        formattedPackageAvailableDate={formattedPackageAvailableDate}
        availabilityPackageBadges={availabilityPackageBadges}
        setIsPackageAvailabilityModalOpen={setIsPackageAvailabilityModalOpen}
        isOnWaitingList={isOnWaitingList}
      />
    );
  }

  const estimatedTransation = generateTransaction(bookingData, listing, hasLodging, packageInfo);

  const totalPrice = get(estimatedTransation, 'attributes.payinTotal', null);
  const formattedTotalPrice = totalPrice ? convertMoneyToNumber(totalPrice) : 0;

  if (onTransactionPriceChange) {
    onTransactionPriceChange(formattedTotalPrice);
  }

  return (
    <Form onSubmit={handleSubmit}>
      <div className={css.formFieldWrapper}>
        <>
          {isFlexiblePricingEnabled(packageInfo.activity) ? (
            <FlexiblePartySizeSelect
              name="partySize"
              activity={packageInfo.activity}
              minPartySize={packageMinParty}
              maxPartySize={packageMaxParty}
              price={packageInfo.price}
              packageFlexiblePricing={packageInfo.flexiblePricing}
              validate={composeValidators(
                required(intl.formatMessage({ id: 'PricingPage.partySizeRequired' }))
              )}
              readOnlyInput
              label={<FormattedMessage id="PricingPage.partySizeLabel" />}
            />
          ) : (
            <FieldPartySizeSelect
              id="partySize"
              name="partySize"
              minPartySize={packageMinParty}
              maxPartySize={packageMaxParty}
              price={JSON.parse(packageInfo.price)}
              parse={value => (value === '' ? null : Number(value))}
              format={value => String(value)}
              validate={composeValidators(
                required(intl.formatMessage({ id: 'PricingPage.partySizeRequired' }))
              )}
              label={<FormattedMessage id="PricingPage.partySizeLabel" />}
            />
          )}
        </>
      </div>

      <div className={css.formFieldWrapper} ref={formFieldWrapperRef}>
        <FieldDates
          fieldDateProps={{
            endDateOffset:
              packageInfo.days.min === packageInfo.days.max
                ? day => day.add(packageInfo.days.min - 1, 'days')
                : undefined,
            minimumNights:
              packageInfo.days.min !== packageInfo.days.max && packageInfo.days.min - 1,
            maximumNights:
              packageInfo.days.max > 0 && packageInfo.days.min !== packageInfo.days.max
                ? packageInfo.days.max - 1
                : undefined,
            calendarInfo: (
              <FormattedMessage
                id="PricingPage.datesFieldInfoPackageTitle"
                values={{ days: calendarInfoDays }}
              />
            ),
            hasLodging: hasLodging || dynamicHasLodging,
            activity: packageInfo.activity,
            containerRef: formFieldWrapperRef,
          }}
          timeSlots={timeSlots}
          label={<FormattedMessage id="PricingPage.datesFieldHeadingPackage" />}
          text={bookingDateText}
          onChange={dates => {
            form.change('dates', dates);
          }}
          isPackage
          onReset={() => {
            form.change('dates', null);
          }}
        />
        {startDate && endDate && (
          <div className={css.availableDates}>
            <FormattedMessage
              id="InquiryWizard.selectedDate"
              values={{ dates: formattedStartDate === formattedEndDate ? 1 : 0 }}
            />
          </div>
        )}
      </div>

      {startDate && endDate && (
        <PackageItinerary
          activity={packageInfo.activity}
          listing={listing}
          startDate={moment(startDate).format('YYYY-MM-DD')}
          endDate={moment(endDate).format('YYYY-MM-DD')}
          hasLodging={hasLodging || dynamicHasLodging}
          startLodgingDate={
            dateValues?.startLodgingDate
              ? moment(dateValues.startLodgingDate).format('YYYY-MM-DD')
              : null
          }
        />
      )}

      {showLodgingAddOn && (
        <div className={css.formFieldWrapper}>
          <div className={css.lodgingContainer}>
            <Box mb="var(--spacing-3)">
              <Heading size="2xs" fontWeight="bold">
                <FormattedMessage id="PricingPage.addOnsTitle" />
              </Heading>
            </Box>

            <Box display="flex" flexDirection="column" gap="var(--spacing-2)">
              {optionalLodging.map((item, index) => {
                const uniqueKey = `${item.type}-${item.index || index}`;
                return (
                  <Field key={uniqueKey} name="lodgingAddons" type="checkbox" value={index}>
                    {({ input }) => {
                      return (
                        <AddOnBlock
                          title={item.shortLabel}
                          subtitle={`+ ${item.priceLabel}`}
                          icon={getLodgingIcon(item.type)}
                          checked={input.checked}
                          onClick={() => {
                            const updatedAddons = handleLodgingAddOnChange(
                              index,
                              lodgingAddons || [],
                              dateValues,
                              availableDays,
                              hasLodging,
                              form.change
                            );
                            form.change('lodgingAddons', updatedAddons);
                          }}
                        />
                      );
                    }}
                  </Field>
                );
              })}
            </Box>
          </div>
        </div>
      )}

      <div className={css.formFieldWrapper}>
        {bookingData && (
          <>
            <EstimatedBreakdownMaybe
              bookingData={bookingData}
              listing={listing}
              hasLodging={hasLodging}
              packageInfo={packageInfo}
            />
          </>
        )}
      </div>

      <div className={css.formFieldWrapper}>
        {!startDate || !endDate ? (
          <FormValidationTooltip
            className={css.submitButtonWrapper}
            placement="top"
            body={<FormattedMessage id="PricingPage.fieldRequirements" />}
            title={<FormattedMessage id="PricingPage.fieldRequirementsTitle" />}
          >
            <div className={css.formFieldWrapper}>
              <PrimaryButton
                data-testId="continue-to-payment-button"
                isFullWidth
                type="submit"
                inProgress={inProgress}
                disabled={submitDisabled}
              >
                <FormattedMessage id="PricingPage.continueToPayment" />
              </PrimaryButton>
            </div>
          </FormValidationTooltip>
        ) : (
          <PrimaryButton
            data-testId="continue-to-payment-button"
            isFullWidth
            type="submit"
            inProgress={inProgress}
            disabled={submitDisabled}
          >
            <FormattedMessage id="PricingPage.continueToPayment" />
          </PrimaryButton>
        )}

        {scarcityMessage ? (
          <div className={css.scarcityMessage}>
            <FeatureListItem
              className={css.scarcityFeatureListItem}
              labelSize="sm"
              label={
                <span
                  className={
                    scarcityMessage.isUrgent
                      ? `${css.scarcityFeatureLabel} ${css.scarcityFeatureLabelUrgent}`
                      : css.scarcityFeatureLabel
                  }
                >
                  <FormattedMessage id={scarcityMessage.id} values={scarcityMessage.values} />
                </span>
              }
            />
          </div>
        ) : null}

        {typeof onMessage === 'function' && (
          <Button
            type="button"
            variant="secondary"
            fullWidth
            className={css.messageLandownerButton}
            onClick={messageWithSelection}
            disabled={isOwnListing}
            data-testid="desktop-message-landowner-button"
          >
            <FormattedMessage
              id="ListingPage.contactLandowner"
              values={{ name: landownerFirstName || 'Landowner' }}
            />
          </Button>
        )}
      </div>

      {submitError ? <div className={css.error}>{submitError}</div> : null}

      {showCredova && (
        <CredovaPlugin price={formattedTotalPrice} onModalOpen={setIsCredovaModalOpen} />
      )}
    </Form>
  );
};

export default PackagePricingForm;
